import { useEffect } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBulkAction, getBulkActions } from 'api/bulkActions';
import {
    injectResourceFilter,
    injectStatusFilter,
} from 'assets/js/pages/contacts/bulk-actions/utils';
import { BulkActionResource, BulkActionStatus, BulkActionType } from 'constants/bulkActions';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { last } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { BulkAction } from 'types/domain';

const BulkActionsNotice = () => {
    const { success } = useToast();
    const { accountId } = useCurrentAccount();
    const pendingBulkActionParams = {
        filters: [
            ...injectResourceFilter([BulkActionResource.CONTACTS]),
            ...injectStatusFilter([BulkActionStatus.PENDING, BulkActionStatus.PROCESSING]),
        ],
    };
    const { data: pendingBulkAction } = useSuspenseQuery({
        queryKey: accountQueryKeys.bulkActionsWithFilters(accountId, pendingBulkActionParams),
        queryFn: () => getBulkActions(accountId, pendingBulkActionParams),
        select: data => last(data.data),
    });

    const pendingBulkActionId = pendingBulkAction?.id ?? -1;

    const { data: bulkAction } = useSuspenseQuery({
        queryKey: accountQueryKeys.bulkActionById(accountId, pendingBulkActionId),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => getBulkAction(accountId, pendingBulkActionId),
                !!pendingBulkAction,
            ),
        refetchInterval: pendingBulkAction ? 2000 : undefined,
        refetchOnWindowFocus: 'always',
    });

    useEffect(() => {
        if (!bulkAction) return;
        if ([BulkActionStatus.FAILED, BulkActionStatus.COMPLETED].includes(bulkAction.status)) {
            success(<CompletedBulkActionText bulkAction={bulkAction} />);
            queryClient.resetQueries({ queryKey: accountQueryKeys.bulkActions(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
            });
        }
    }, [bulkAction, accountId, success]);

    if (!pendingBulkAction) {
        return null;
    }

    return (
        <AlertNotification.Root type="warning">
            <div className="size-4">
                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
            </div>
            <AlertNotification.Content>
                <AlertNotification.Title className="text-base">
                    In progress: <PendingBulkActionNoticeText bulkAction={pendingBulkAction} />
                </AlertNotification.Title>
            </AlertNotification.Content>
        </AlertNotification.Root>
    );
};

const PendingBulkActionNoticeText = ({ bulkAction }: { bulkAction: BulkAction }) => {
    const recordCount = bulkAction.record_count;
    const pluralizedResource = pluralize(recordCount, 'contact');

    switch (bulkAction.type) {
        case BulkActionType.ADD_CONTACT_TAGS:
            return `Adding tags to ${recordCount} ${pluralizedResource}`;
        case BulkActionType.REMOVE_CONTACT_TAGS:
            return `Removing tags from ${recordCount} ${pluralizedResource}`;
        case BulkActionType.ARCHIVE_CONTACTS:
            return `Archiving ${recordCount} ${pluralizedResource}`;
        case BulkActionType.UNARCHIVE_CONTACTS:
            return `Unarchiving ${recordCount} ${pluralizedResource}`;
        case BulkActionType.DELETE_CONTACTS:
            return `Deleting ${recordCount} ${pluralizedResource}`;
        case BulkActionType.MANAGE_CONTACT_ENGAGE_SUBSCRIPTIONS:
            return `Updating subscription status for ${recordCount} ${pluralizedResource}`;
    }
};

const CompletedBulkActionText = ({ bulkAction }: { bulkAction: BulkAction }) => {
    const completedCount = bulkAction.completed_count ?? 0;
    const failedCount = bulkAction.record_count - completedCount;

    switch (bulkAction.type) {
        case BulkActionType.ADD_CONTACT_TAGS:
            return `Adding tags completed: ${completedCount} items tagged successfully, ${failedCount} failed.`;
        case BulkActionType.REMOVE_CONTACT_TAGS:
            return `Removing tags completed: ${completedCount} items untagged successfully, ${failedCount} failed.`;
        case BulkActionType.ARCHIVE_CONTACTS:
            return `Archiving completed: ${completedCount} items archived successfully, ${failedCount} failed.`;
        case BulkActionType.UNARCHIVE_CONTACTS:
            return `Unarchiving completed: ${completedCount} items unarchived successfully, ${failedCount} failed.`;
        case BulkActionType.DELETE_CONTACTS:
            return `Deleting completed: ${completedCount} items deleted successfully, ${failedCount} failed.`;
        case BulkActionType.MANAGE_CONTACT_ENGAGE_SUBSCRIPTIONS:
            return `Updating subscription status completed: ${completedCount} items updated successfully, ${failedCount} failed.`;
    }
};

export { BulkActionsNotice };
